import clsx from 'clsx';
import { debounce, isArray, isEmpty, toNumber, toString } from 'lodash';
import { object, shape } from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  AddCartSuccessModal,
  Card,
  FormInput,
  FormInputCount,
  FormSelect,
  Icon,
  Loader,
  Skeleton,
  StrapiImg
} from 'components';
import { PAGE_TYPES } from 'constants';
import { addUuid, convertToCents, formatLocalizedCurrency } from 'helpers';
import { useFetch, useSafeState, useScreenResize, useUser } from 'hooks';
import { appService } from 'services';
import { setCart } from 'store';

import defaultArrivajImg from 'assets/img/default-arrivaj-img.png';
import defaultTouloisirsImg from 'assets/img/default-touloisirs-img.png';
import 'assets/styles/components/products/leisure-show.scss';

const MAX_QUANTITY = 99;
const GIFTCARD_COMMISSION_PERCENTAGE = 5;

const GiftcardSummary = ({ giftcard }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [totalPrice, setTotalPrice] = useSafeState(0);
  const [openAmountError, setOpenAmountError] = useSafeState({ min: false, max: false });
  const [confirmationModal, setConfirmationModal] = useSafeState({ open: false, content: null, alert: null });
  const [fixedAmountOptions, setFixedAmountOptions] = useSafeState([]);
  const [showDiscountLabel, setShowDiscountLabel] = useSafeState(false);

  const { call: addToCartCall, loading: cartLoading } = useFetch();
  const { call: fixedAmountsCall, loading: fixedAmountsLoading } = useFetch({ withSetState: false });
  const { call: openAmountCall, loading: openAmountLoading } = useFetch({ withSetState: false });
  const { currentUser } = useUser();
  const { showElement, setShowElement } = useScreenResize();
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      fixedAmount: null,
      openAmount: null,
      quantity: 1
    }
  });

  const selectedAmount = form.watch('fixedAmount');
  const selectedOpenAmount = form.watch('openAmount');
  const selectedQuantity = form.watch('quantity');

  const giftcardPicture = useMemo(() => {
    if (!giftcard?.picture) {
      return {
        frontUrl: appService.isTouloisirs() ? defaultTouloisirsImg : defaultArrivajImg
      };
    }

    return giftcard.picture;
  }, [giftcard]);

  const isOpenAmount = useMemo(() => selectedAmount?.value === 'openAmount', [selectedAmount]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getFixedAmounts();
  }, [giftcard]);

  useEffect(() => {
    // Prevent selection above max quantity
    if (selectedQuantity > MAX_QUANTITY) {
      form.setValue('quantity', MAX_QUANTITY);
    }
  }, [selectedQuantity]);

  useEffect(() => {
    // Reset total price and previously selected open amount when selecting open amount option
    if (isOpenAmount) {
      setTotalPrice(0);
      form.setValue(
        'fixedAmount',
        {
          name: 'fixedAmount',
          value: 'openAmount',
          label: t('products:giftcards.otherAmount')
        },
        { shouldValidate: true }
      );
      form.setValue('openAmount', null, { shouldValidate: true });
    }
  }, [isOpenAmount]);

  useEffect(() => {
    /**
     * Calculate total price when amount or quantity changes
     * with debounce for open amount to prevent multiple calculations
     */
    isOpenAmount ? debounceAmountChange(selectedOpenAmount, selectedQuantity) : handleFixedAmountChange();
  }, [giftcard, selectedAmount, selectedOpenAmount, selectedQuantity]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getFixedAmounts = async () => {
    if (!giftcard?.giftCardId) return;

    const { data } =
      (await fixedAmountsCall({
        service: 'buybox',
        method: 'get',
        path: `/api/giftcards/merchants/${giftcard.giftCardId}/fixed-prices`
      })) || {};

    if (isArray(data?.body)) {
      let options = [];
      options = data.body.map((fixedAmount) => {
        return {
          name: 'fixedAmount',
          value: fixedAmount.price,
          amount: fixedAmount.amount,
          label: (
            <div>
              {formatLocalizedCurrency(fixedAmount.price / 100)}&nbsp;
              <span className="font-bold">
                {t('products:giftcards.amount', { amount: formatLocalizedCurrency(fixedAmount.amount / 100) })}
              </span>
            </div>
          )
        };
      });

      if (giftcard.openValue) {
        options.push({
          name: 'fixedAmount',
          value: 'openAmount',
          label: t('products:giftcards.otherAmount')
        });
      }

      if (options.length > 0) setFixedAmountOptions(addUuid(options));
    } else if (data?.status === 400) {
      // Fixed amounts are not available, set default value as openAmount
      form.setValue('fixedAmount', { value: 'openAmount' });
      form.setValue('openAmount', null);
    }
  };

  const debounceAmountChange = useCallback(
    debounce((amount, quantity) => handleOpenAmountChange(amount, quantity), 800),
    []
  );

  const handleOpenAmountChange = async (amount, quantity) => {
    amount = toNumber(amount || 0);

    if (amount) {
      setOpenAmountError({
        min: amount < giftcard.minAmount,
        max: amount > giftcard.maxAmount
      });

      if (amount >= giftcard.minAmount && amount <= giftcard.maxAmount) {
        setShowDiscountLabel(false);

        const { data } =
          (await openAmountCall({
            service: 'buybox',
            method: 'post',
            path: `/api/giftcards/merchants/${giftcard.giftCardId}/price`,
            data: { amount: convertToCents(amount) }
          })) || {};

        if (data?.status !== 200) {
          toast.error(t('products:giftcards.openAmountError'));
        }

        if (data?.status === 200 && !!data?.body?.price) {
          const price = (data.body.price / 100) * quantity;
          setTotalPrice(price);
          setShowDiscountLabel(data.body.price !== data.body.amount);
        }
      }
    } else {
      setOpenAmountError({ min: false, max: false });
    }
  };

  const handleFixedAmountChange = () => {
    if (!selectedAmount?.value) return;
    const amount = (selectedAmount.value / 100) * selectedQuantity;
    setTotalPrice(amount);
  };

  const addToCart = async () => {
    if (cartLoading || openAmountLoading || !giftcard) return;
    setShowElement((prev) => ({ ...prev, mobile: false }));
    const amount = isOpenAmount ? convertToCents(toNumber(selectedOpenAmount)) : selectedAmount?.amount;

    const payload = {
      lines: [
        {
          productId: toString(giftcard.giftCardId),
          typeItem: 'BUYBOX',
          unitPrice: convertToCents(totalPrice / selectedQuantity),
          quantity: selectedQuantity,
          categoryId: giftcard.category.slug,
          options: {
            optionsType: 'BUYBOX',
            label: giftcard.title,
            merchantId: giftcard.giftCardId,
            cardValue: amount
          }
        }
      ]
    };

    const { data } =
      (await addToCartCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser.serialNumber}/cart/ticketing/products`,
        data: payload
      })) || {};

    if (data?.status === 200 && !isEmpty(data?.body)) {
      const productContent = [
        {
          label: giftcard.title,
          quantity: selectedQuantity || 0,
          total: totalPrice,
          vignette: giftcardPicture,
          typeItem: PAGE_TYPES.giftcards
        }
      ];

      setConfirmationModal({
        open: true,
        content: productContent,
        alert: data.body?.alert || data.body?.notifications?.[0]?.message
      });
      dispatch(setCart(data.body));
      resetForm();
    } else if (data?.status === 400) {
      toast.error(t('cart:addError'));
    }
  };

  const resetForm = () => {
    form.reset({ openAmount: null, quantity: 1 });
    if (isEmpty(fixedAmountOptions)) form.setValue('fixedAmount', { value: 'openAmount' });
    setTotalPrice(0);
    setShowDiscountLabel(false);
  };

  if (!giftcard) return null;

  return (
    <>
      <div className="w-full print:hidden">
        <div className="hidden lg:block w-full h-56 overflow-hidden">
          <StrapiImg image={giftcardPicture} width="116" height="200" className="w-full h-full object-cover" />
        </div>

        <div className="hidden lg:flex lg:items-center lg:justify-between mt-2 mb-6">
          {giftcard?.availableInStore && (
            <div className="flex items-center gap-2">
              <Icon name="bag-shopping" iconStyle="fad" classes="w-6 h-6 text-yellow-500" />
              <span>{t('products:giftcards.useInStore')}</span>
            </div>
          )}

          {giftcard?.availableOnline && (
            <div className="flex items-center gap-2">
              <Icon name="laptop" iconStyle="fad" classes="w-6 h-6 text-yellow-500" />
              <span>{t('products:giftcards.useOnline')}</span>
            </div>
          )}
        </div>

        <div
          className={clsx('leisure-overlay', showElement.mobile && 'open-mobile')}
          onClick={() => setShowElement((prev) => ({ ...prev, mobile: false }))}
        />

        {fixedAmountsLoading && <Skeleton size="initial" classNames="w-full h-64" />}

        {!fixedAmountsLoading && (
          <Card
            onMobile={false}
            onTablet={false}
            className={clsx('giftcard-summary', showElement.mobile && 'open-mobile')}
          >
            {(showElement.mobile || showElement.desktop) && (
              <FormProvider {...form}>
                <form className="flex flex-col gap-8" onSubmit={form.handleSubmit(addToCart)}>
                  <div>
                    {!isEmpty(fixedAmountOptions) && (
                      <>
                        <Controller
                          name="fixedAmount"
                          control={form.control}
                          render={({ field: { onChange } }) => (
                            <FormSelect
                              options={fixedAmountOptions}
                              onChange={onChange}
                              isLoading={fixedAmountsLoading}
                              label={t('products:giftcards.yourGiftcard')}
                              name="fixedAmount"
                              disabled={cartLoading || openAmountLoading}
                            />
                          )}
                        />
                        <p className={clsx('text-sm', 'text-gray-500', 'mt-1')}>
                          {t('products:giftcards.description')}
                        </p>
                      </>
                    )}
                    {isOpenAmount && (
                      <div>
                        <FormInput
                          type="number"
                          name="openAmount"
                          label={t('products:giftcards.openAmountLabel')}
                          placeholder={t('products:giftcards.openAmountPlaceholder')}
                          min={giftcard.minAmount}
                          max={giftcard.maxAmount}
                          rules={{ min: giftcard.minAmount, max: giftcard.maxAmount }}
                          showError={false}
                          disabled={openAmountLoading}
                        />

                        <div className="flex flex-wrap justify-between gap-2 mt-2">
                          <p className={clsx('text-sm', openAmountError.min ? 'text-red-500' : 'text-gray-500')}>
                            {t('products:giftcards.minAmount', {
                              min: formatLocalizedCurrency(giftcard.minAmount, {
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0,
                                style: 'currency',
                                currency: 'EUR'
                              })
                            })}
                          </p>
                          <p className={clsx('text-sm', openAmountError.max ? 'text-red-500' : 'text-gray-500')}>
                            {t('products:giftcards.maxAmount', {
                              max: formatLocalizedCurrency(giftcard.maxAmount, {
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0,
                                style: 'currency',
                                currency: 'EUR'
                              })
                            })}
                          </p>
                        </div>
                        <p className={clsx('text-sm', 'text-gray-500', 'mt-1')}>
                          {t('products:giftcards.description')}
                        </p>
                      </div>
                    )}
                  </div>

                  <FormInputCount
                    name="quantity"
                    label={t('form:quantity')}
                    min={1}
                    max={MAX_QUANTITY}
                    rules={{ min: 1, max: MAX_QUANTITY }}
                    disabled={cartLoading || openAmountLoading}
                  />

                  {/* PRICES RESUME */}
                  <div className="border-t border-gray-200 pt-4 print:p-3">
                    <div className="flex justify-between mb-2 gap-x-2 md:mb-4">
                      <p className="flex flex-wrap gap-1 items-baseline">
                        <span>{t('products:netPrice')}</span>
                        {isOpenAmount && showDiscountLabel && (
                          <span className="text-sm">
                            {t('products:giftcards.discountHint', { discount: GIFTCARD_COMMISSION_PERCENTAGE })}
                          </span>
                        )}
                      </p>
                      {openAmountLoading ? (
                        <Loader loaderClass="w-4 h-4 text-primary-500" />
                      ) : (
                        <span className="font-bold">{formatLocalizedCurrency(totalPrice)}</span>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn uppercase mx-auto mt-4 print:hidden"
                      disabled={!form.formState.isValid || cartLoading || openAmountLoading}
                    >
                      {(cartLoading || openAmountLoading) && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
                      {t('products:addToCart')}
                    </button>
                  </div>
                </form>
              </FormProvider>
            )}
          </Card>
        )}

        {/* MOBILE VERSION ONLY */}
        <div className={clsx('bg-white w-full lg:hidden print:hidden', showElement.mobile && 'hidden')}>
          <button
            type="button"
            className="btn mx-auto"
            onClick={() => setShowElement((prev) => ({ ...prev, mobile: true }))}
            disabled={!form.formState.isValid || cartLoading || openAmountLoading}
          >
            {(cartLoading || openAmountLoading) && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
            {t('products:addToCart')}
          </button>
        </div>
      </div>

      {/* PRINT VERSION ONLY */}
      {totalPrice > 0 && (
        <div className="hidden print:flex print:flex-col print:gap-4">
          <h3 className="h2 underlined-yellow">{t('products:giftcards.yourGiftcard')}</h3>
          <ul>
            <li className="list-disc">
              {t(`products:giftcards.${isOpenAmount ? 'openAmountValue' : 'amountLabel'}`, {
                discountAmount: formatLocalizedCurrency(totalPrice),
                amount: formatLocalizedCurrency(isOpenAmount ? selectedOpenAmount : selectedAmount?.value)
              })}
            </li>
            <li className="list-disc">{t('products:giftcards.quantityLabel', { quantity: selectedQuantity })}</li>
            <li className="list-disc font-bold">
              {t(`products:giftcards.${isOpenAmount ? 'netPriceLabelWithDiscount' : 'netPriceLabel'}`, {
                price: formatLocalizedCurrency(totalPrice),
                discount: GIFTCARD_COMMISSION_PERCENTAGE
              })}
            </li>
          </ul>
        </div>
      )}

      <AddCartSuccessModal
        confirmationModal={confirmationModal}
        setConfirmationModal={setConfirmationModal}
        pageType={PAGE_TYPES.giftcards}
      />
    </>
  );
};

GiftcardSummary.propTypes = {
  giftcard: shape({
    picture: object
  }).isRequired
};

export default GiftcardSummary;
